import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SmsModal } from "@features/SmsModal/SmsModal";
import { Button } from "@components/Button";
import { ReceiptIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { useModalActions, type IUseModalProps } from "@store/modal/modal";
import { useOverviewQuery } from "@services/overview/queries";
import { type IOverviewJointBillService } from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import { sanitizeHtml } from "@utils/html";

interface IPlanChangedModalProps extends IUseModalProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  onAgreeClick?: () => void;
  isPlanChangeOptional?: boolean;
}

export const PlanChangedModal: React.FC<IPlanChangedModalProps> = ({
  jointBillServiceId,
  onAgreeClick,
  isPlanChangeOptional = true,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const { create } = useModalActions();
  const modal = useModal(modalProps);
  const { data: overview } = useOverviewQuery();
  const house = overview?.jointBillServices?.find(
    (jbs) => jbs.jointBillServiceId === jointBillServiceId,
  );
  const perks = useTranslationArray("planChangedModal.perks");

  const openSmsModal = useCallback(() => {
    modal.onClose?.();

    if (!isPlanChangeOptional) {
      create((modalProps) => (
        <SmsModal {...modalProps} jointBillServiceId={jointBillServiceId} />
      ));
    }
  }, [isPlanChangeOptional, jointBillServiceId, modal, create]);

  const onSubmitClick = () => {
    onAgreeClick?.();

    if (isPlanChangeOptional) {
      create((modalProps) => (
        <SmsModal {...modalProps} jointBillServiceId={jointBillServiceId} />
      ));
    }

    modal.close();
  };

  return (
    <Modal
      modal={{
        ...modal,
        onClose: openSmsModal,
      }}
      badgeProps={{ icon: ReceiptIcon }}
      title={t("planChangedModal.title")}
      subtitle={t("planChangedModal.subtitle")}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" onClick={onSubmitClick}>
            {t("planChangedModal.actions.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <ul>
          {perks?.map((perk, index) => (
            <li
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(perk) }}
              key={index}
            />
          ))}
        </ul>
        {!isNullOrUndefined(house) && (
          <div>
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t("planChangedModal.validForHouse", {
                    houseName: house.displayName ?? t("common.defaultAddress"),
                  }),
                ),
              }}
            />
          </div>
        )}
        <div
          className="text-sm text-grey-dark"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(t("planChangedModal.refuseDescription")),
          }}
        />
      </div>
    </Modal>
  );
};
